import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, Text, TextInput, View, type ViewStyle } from "react-native";

import { listMyCompetitions } from "../../../../../lib/clientCompetitions";
import { listTopCandidates, type RecentCandidateRow } from "../../../../../lib/clientDashboard";
import { resolveActiveCaseId, writeActiveCaseId } from "../../../../../lib/useActiveCase";
import { useRequireApprovedCase } from "../../../../../lib/useRequireApprovedCase";
import type { ClientCompetitionListRow } from "../../../../../types/database";
import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";

const THEME = PANE_THEMES.Client;
const AMBER = THEME.accent;
const OFF_BLACK = THEME.surfaceBg;
const W = "rgba(255,255,255,1)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const SAGE = COLORS.SAGE.hex;

type SortKey = "recent" | "name" | "team" | "submissions";

export default function ClientCaseCandidatesScreen() {
  // Route guard — pre-approval cases bounce to /edit (no candidates yet).
  useRequireApprovedCase();
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const urlCaseId = Array.isArray(params.id) ? params.id[0] : params.id;

  const [rows, setRows] = useState<ClientCompetitionListRow[]>([]);
  const [candidates, setCandidates] = useState<RecentCandidateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listMyCompetitions();
      setRows(list);

      const fromUrl = urlCaseId && list.some((r) => r.id === urlCaseId) ? urlCaseId : null;
      const resolved = fromUrl ?? resolveActiveCaseId(list);
      if (resolved) {
        writeActiveCaseId(resolved);
        const cands = await listTopCandidates(resolved, 200);
        setCandidates(cands);
      } else {
        setCandidates([]);
      }
    } finally {
      setLoading(false);
    }
  }, [urlCaseId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  // Redirect if URL caseId is invalid.
  const activeCase = urlCaseId ? rows.find((r) => r.id === urlCaseId) ?? null : null;
  const showInvalid = !loading && !!urlCaseId && rows.length > 0 && !activeCase;
  if (showInvalid) {
    queueMicrotask(() => router.replace("/client" as Href));
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let arr = candidates.slice();
    if (q) {
      arr = arr.filter((c) => {
        const hay = [c.full_name, c.university, c.major, c.team_name].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    arr.sort((a, b) => {
      switch (sort) {
        case "name":
          return (a.full_name ?? "").localeCompare(b.full_name ?? "");
        case "team":
          return (a.team_name ?? "\uFFFF").localeCompare(b.team_name ?? "\uFFFF");
        case "submissions":
          return b.submitted_count - a.submitted_count;
        case "recent":
        default:
          return 0; // already in recent order from server
      }
    });
    return arr;
  }, [candidates, query, sort]);

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 32, paddingTop: 28, paddingBottom: 60 }}>
        {/* Hero */}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Case roster
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.display,
            fontSize: 32,
            lineHeight: 36,
            color: W,
          }}
          numberOfLines={1}
        >
          {activeCase?.name ?? "Candidates"}
        </Text>
        <Text
          style={{
            marginTop: 6,
            fontFamily: FONTS.bodyLight,
            fontSize: 13.5,
            lineHeight: 20,
            color: WM,
            maxWidth: 720,
          }}
        >
          Every student registered for this case, with their team and submission progress. Click a row to open
          their full profile.
        </Text>

        {/* Controls */}
        <View
          style={{
            marginTop: 24,
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
          }}
        >
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Search name, school, major, or team..."
            placeholderTextColor={WL}
            style={{
              flexGrow: 1,
              minWidth: 240,
              maxWidth: 420,
              backgroundColor: OFF_BLACK,
              borderWidth: 1,
              borderColor: BORDER,
              borderRadius: 3,
              paddingHorizontal: 14,
              paddingVertical: 10,
              color: W,
              fontFamily: FONTS.body,
              fontSize: 13,
            }}
          />
          <View style={{ flexDirection: "row", gap: 6, flexWrap: "wrap" }}>
            {([
              ["recent", "Most recent"],
              ["name", "A\u2013Z"],
              ["team", "By team"],
              ["submissions", "Most submissions"],
            ] as Array<[SortKey, string]>).map(([k, label]) => {
              const active = sort === k;
              return (
                <Pressable
                  key={k}
                  onPress={() => setSort(k)}
                  style={[
                    {
                      paddingHorizontal: 12,
                      paddingVertical: 7,
                      borderRadius: 999,
                      borderWidth: 1,
                      borderColor: active ? AMBER : BORDER,
                      backgroundColor: active ? "rgba(181,135,58,0.12)" : "transparent",
                    } as ViewStyle,
                    Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyMedium,
                      fontSize: 11,
                      letterSpacing: 0.4,
                      color: active ? AMBER : WM,
                    }}
                  >
                    {label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
          <Text
            style={{
              marginLeft: "auto",
              fontFamily: FONTS.bodyMedium,
              fontSize: 11.5,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: WM,
            }}
          >
            {loading ? "Loading\u2026" : `${filtered.length} of ${candidates.length}`}
          </Text>
        </View>

        {/* Rows */}
        <View
          style={{
            marginTop: 18,
            backgroundColor: OFF_BLACK,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          {loading ? (
            <View style={{ padding: 24 }}>
              <Text style={{ color: WM, fontFamily: FONTS.body, fontSize: 13 }}>
                Loading candidates…
              </Text>
            </View>
          ) : filtered.length === 0 ? (
            <View style={{ padding: 28 }}>
              <Text
                style={{
                  fontFamily: FONTS.display,
                  fontSize: 22,
                  color: W,
                }}
              >
                {query ? "No matches." : "No candidates yet."}
              </Text>
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: WM,
                  maxWidth: 540,
                }}
              >
                {query
                  ? "Try a broader search \u2014 we look across name, school, major, and team."
                  : "When students register for this case they\u2019ll show up here with their team and submission progress."}
              </Text>
            </View>
          ) : (
            filtered.map((c, i) => (
              <Pressable
                key={c.student_id}
                onPress={() => router.push(`/client/student/${c.student_id}` as Href)}
                style={[
                  {
                    paddingHorizontal: 18,
                    paddingVertical: 14,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 14,
                    borderTopWidth: i === 0 ? 0 : 1,
                    borderTopColor: BORDER,
                  } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                {/* Avatar */}
                <View
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 19,
                    backgroundColor: "rgba(181,135,58,0.14)",
                    borderWidth: 1,
                    borderColor: "rgba(181,135,58,0.4)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11.5, color: AMBER, letterSpacing: 1 }}>
                    {c.initials}
                  </Text>
                </View>

                {/* Identity */}
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    numberOfLines={1}
                    style={{ fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: W, letterSpacing: 0.1 }}
                  >
                    {c.full_name ?? "(no name)"}
                  </Text>
                  <Text
                    numberOfLines={1}
                    style={{ marginTop: 3, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}
                  >
                    {[c.major, c.university].filter(Boolean).join(" \u00B7 ") || "\u2014"}
                  </Text>
                </View>

                {/* Team */}
                <View style={{ minWidth: 140, maxWidth: 180 }}>
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 8.5,
                      letterSpacing: 1.2,
                      textTransform: "uppercase",
                      color: WL,
                    }}
                  >
                    Team
                  </Text>
                  <Text
                    numberOfLines={1}
                    style={{ marginTop: 2, fontFamily: FONTS.bodyMedium, fontSize: 12, color: c.team_name ? W : WL }}
                  >
                    {c.team_name ?? "\u2014 No team"}
                  </Text>
                </View>

                {/* Submissions */}
                <View style={{ minWidth: 90, alignItems: "flex-end" }}>
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 8.5,
                      letterSpacing: 1.2,
                      textTransform: "uppercase",
                      color: WL,
                    }}
                  >
                    Submissions
                  </Text>
                  <Text style={{ marginTop: 2, fontFamily: FONTS.displayMedium, fontSize: 18, color: W }}>
                    {c.submitted_count}
                  </Text>
                </View>

                {/* Status pill */}
                <View
                  style={{
                    paddingHorizontal: 9,
                    paddingVertical: 4,
                    borderRadius: 999,
                    backgroundColor:
                      c.status === "submitted" ? "rgba(143,187,138,0.14)" : "rgba(181,135,58,0.12)",
                    borderWidth: 1,
                    borderColor: c.status === "submitted" ? "rgba(143,187,138,0.4)" : "rgba(181,135,58,0.4)",
                  }}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 8.5,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                      color: c.status === "submitted" ? SAGE : AMBER,
                    }}
                  >
                    {c.status === "submitted" ? "Submitted" : "In progress"}
                  </Text>
                </View>
              </Pressable>
            ))
          )}
        </View>
    </ScrollView>
  );
}
